import { Link, useLocation, Navigate } from 'react-router-dom'

interface RoleData {
  title: string
  jobFamily: string
  levelMin: string
  levelMax: string
  locations: string[]
  principles: { id: string; name: string; enabled: boolean }[]
  defaultSalary?: { min: string; max: string }
  salaryOverrides?: { location: string; min: string; max: string }[]
  barRaiserRequired: boolean
}

// Icons
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  )
}

function DocumentPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  )
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  )
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
    </svg>
  )
}

const NEXT_ACTIONS = [
  {
    id: 'candidates',
    icon: DocumentPlusIcon,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    title: 'Add candidates',
    description: 'Upload resumes, paste LinkedIn profiles, or import from your ATS',
    href: '/candidates/new',
    primary: true,
  },
  {
    id: 'evaluators',
    icon: UsersIcon,
    iconBg: 'bg-brand-100',
    iconColor: 'text-brand-600',
    title: 'Invite evaluators',
    description: 'Bring in teammates to contribute structured feedback',
    href: '/evaluators/invite',
    primary: false,
  },
]

export default function RoleReadyPage() {
  const location = useLocation()
  const roleData = location.state?.role as RoleData | undefined

  // Redirect if no role data (user navigated directly)
  if (!roleData) {
    return <Navigate to="/roles/new" replace />
  }

  // Format level display
  const levelDisplay = roleData.levelMin === roleData.levelMax
    ? roleData.levelMin.charAt(0).toUpperCase() + roleData.levelMin.slice(1)
    : `${roleData.levelMin.charAt(0).toUpperCase() + roleData.levelMin.slice(1)} → ${roleData.levelMax.charAt(0).toUpperCase() + roleData.levelMax.slice(1)}`

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/40 via-white to-brand-50/20 flex flex-col">
      {/* Ambient background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-bl from-emerald-100/40 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-brand-50/50 via-transparent to-transparent rounded-full blur-3xl" />
        {/* Celebration particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full opacity-20"
              style={{
                backgroundColor: ['#10b981', '#8b5cf6', '#f59e0b', '#06b6d4'][i % 4],
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `roleReadyFloat ${3 + Math.random() * 4}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-surface-200/60 bg-white/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5 group">
            <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8 transition-transform group-hover:scale-105" />
            <span className="font-display font-semibold text-lg text-surface-900">Jumpstone</span>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        {/* Success indicator */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-emerald-400/20 rounded-full blur-2xl scale-[2] animate-pulse" />
          <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30 ring-8 ring-emerald-100/50">
            <CheckCircleIcon className="w-10 h-10 text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-surface-900 mb-3 text-center">
          This role is ready for evaluation.
        </h1>
        <p className="text-lg text-surface-500 mb-8 text-center max-w-md">
          Your decision frame is set. Now bring in the evidence.
        </p>

        {/* Role summary card */}
        <div className="w-full max-w-lg bg-white rounded-2xl border border-surface-200 shadow-sm mb-10 overflow-hidden">
          <div className="p-6 border-b border-surface-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-surface-900">{roleData.title}</h2>
                <p className="text-surface-500 mt-1">
                  {roleData.jobFamily} · {levelDisplay}
                </p>
              </div>
              <div className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                Ready
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {/* Locations */}
            <div className="flex items-start gap-3">
              <MapPinIcon className="w-5 h-5 text-surface-400 mt-0.5 shrink-0" />
              <div className="flex flex-wrap gap-2">
                {roleData.locations.map((loc) => (
                  <span
                    key={loc}
                    className="px-2.5 py-1 bg-surface-100 text-surface-700 text-sm rounded-md"
                  >
                    {loc}
                  </span>
                ))}
              </div>
            </div>

            {/* Principles */}
            <div className="flex items-start gap-3">
              <SparklesIcon className="w-5 h-5 text-surface-400 mt-0.5 shrink-0" />
              <div className="flex flex-wrap gap-2">
                {roleData.principles.map((principle) => (
                  <span
                    key={principle.id}
                    className="px-2.5 py-1 bg-amber-50 text-amber-700 text-sm rounded-md border border-amber-200"
                  >
                    {principle.name}
                  </span>
                ))}
              </div>
            </div>

            {/* Salary range if set */}
            {roleData.defaultSalary && (roleData.defaultSalary.min || roleData.defaultSalary.max) && (
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-surface-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <span className="px-2.5 py-1 bg-cyan-50 text-cyan-700 text-sm rounded-md border border-cyan-200">
                    {roleData.defaultSalary.min && roleData.defaultSalary.max 
                      ? `${roleData.defaultSalary.min} – ${roleData.defaultSalary.max}`
                      : roleData.defaultSalary.min || roleData.defaultSalary.max
                    }
                  </span>
                  {roleData.salaryOverrides && roleData.salaryOverrides.length > 0 && (
                    <span className="ml-2 text-xs text-surface-400">
                      + {roleData.salaryOverrides.length} location override{roleData.salaryOverrides.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Bar-raiser if enabled */}
            {roleData.barRaiserRequired && (
              <div className="flex items-center gap-3 pt-2">
                <div className="w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                  <svg className="w-3 h-3 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm text-violet-700">Bar-raiser review required</span>
              </div>
            )}
          </div>
        </div>

        {/* Next actions */}
        <div className="w-full max-w-lg">
          <h3 className="text-sm font-semibold text-surface-500 uppercase tracking-wider mb-4 text-center">
            Next steps
          </h3>
          <div className="space-y-3">
            {NEXT_ACTIONS.map((action, index) => {
              const Icon = action.icon
              return (
                <Link
                  key={action.id}
                  to={action.href}
                  className={`
                    group flex items-center gap-4 p-5 rounded-2xl border transition-all duration-200
                    ${action.primary
                      ? 'bg-gradient-to-r from-emerald-50 to-white border-emerald-200 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/10'
                      : 'bg-white border-surface-200 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/5'
                    }
                  `}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`w-12 h-12 rounded-xl ${action.iconBg} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 ${action.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${action.primary ? 'text-emerald-900' : 'text-surface-900'} group-hover:text-brand-700 transition-colors`}>
                      {action.title}
                    </p>
                    <p className="text-sm text-surface-500">{action.description}</p>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-surface-400 group-hover:text-brand-600 group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              )
            })}
          </div>
        </div>

        {/* Dashboard link */}
        <div className="mt-10">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-surface-600 hover:text-surface-900 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to dashboard
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-surface-100 bg-white/60 backdrop-blur-sm py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-sm text-surface-400">
            Roles define how you evaluate — not what you post.
          </p>
        </div>
      </footer>

      {/* Inline keyframes */}
      <style>{`
        @keyframes roleReadyFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </div>
  )
}

